import { getWorkflowNodeConfig } from "../nodes";
import { TipsPanel } from "./tips-panel";
import { AddNodePanel } from "./add-node-panel";
import {
  FloatingPanel,
  FloatingPanelContent,
  FloatingPanelGroup,
  FloatingPanelHeader,
  FloatingPanelSeparator,
} from "@babackapp/ui/components/floating-panel";
import { useNodeSelection } from "../context/node-selection-context";
import { useAddNodeContext } from "../context/add-node-context";
import useNodePalette from "../hooks/use-node-palette";
import { Trash2 } from "lucide-react";
import { useRemoveNode } from "../hooks/use-remove-node";
import { Button } from "@babackapp/ui/components/button";
import { AnimatePresence, motion } from "motion/react";
import { useIsWorkflowsListingMode } from "../context/workflows-editor-workflows-context";
import { useTranslation } from "react-i18next";


interface RightSidebarProps {
  onUpdateNode?: (nodeId: string, data: unknown) => void;
}

const slideVariants = {
  initial: {
    x: 20,
    opacity: 0
  },
  animate: {
    x: 0,
    opacity: 1
  },
  exit: {
    x: -20,
    opacity: 0
  }
};

const transition = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1] as const // easeOut cubic-bezier
};

export function RightSidebar({ onUpdateNode }: RightSidebarProps) {
  const { selected, clearSelection } = useNodeSelection();
  const { isAddingNode } = useAddNodeContext();
  const { classes } = useNodePalette(selected?.type || "trigger");
  const { removeNode } = useRemoveNode();
  const isWorkflowsListingMode = useIsWorkflowsListingMode();
  const { t } = useTranslation("trackoffice");

  const handleRemoveNode = () => {
    if (!selected) return;
    removeNode(selected.id);
    clearSelection();
  };

  if (isWorkflowsListingMode) return null;

  return (
    <AnimatePresence mode="wait">
      {isAddingNode ? (
        <motion.div
          key="add-node"
          variants={slideVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
          className="z-[50] pointer-events-none"
        >
          <FloatingPanelGroup className="pointer-events-auto">
            <AddNodePanel />
          </FloatingPanelGroup>
        </motion.div>
      ) : selected ? (
        (() => {
          const nodeConfig = getWorkflowNodeConfig(selected.type);
          const Icon = nodeConfig?.config.Icon;
          const ConfigPanel = nodeConfig?.ConfigPanel;

          if (ConfigPanel) {
            return (
              <motion.div
                key={`config-${selected.id}`}
                variants={slideVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={transition}
                className="z-[50] pointer-events-none flex min-h-0 flex-col"
              >
                <FloatingPanelGroup className="pointer-events-auto min-h-0">
                  <FloatingPanel className="flex min-h-0 flex-col">
                    <FloatingPanelHeader className={classes}>
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center">
                          {Icon && <Icon className="mr-2" size={16} />}
                          {t(nodeConfig.config.name, nodeConfig.config.name)}
                        </div>
                        {selected.type !== "trigger" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={handleRemoveNode}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </FloatingPanelHeader>
                    <FloatingPanelSeparator />
                    <FloatingPanelContent className="min-h-0 overflow-y-auto">
                      <ConfigPanel
                        key={selected.id}
                        nodeId={selected.id}
                        data={selected.data}
                        onUpdate={(newData) => onUpdateNode?.(selected.id, newData)}
                      />
                    </FloatingPanelContent>
                  </FloatingPanel>
                </FloatingPanelGroup>
                <TipsPanel removeIfHidden />
              </motion.div>
            );
          }
          return null;
        })()
      ) : (
        <motion.div
          key="tips"
          variants={slideVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
        >
          <TipsPanel />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
